import { type CountryCode } from "@leabankab/component-library";
import type { CacheForms } from "@/types/cache-forms";
import type { Applicant } from "@/types/form";
import {
	type NationalIdCountry,
	normalizeNationalId,
} from "@/utils/validation/normalize-national-id";
import { validateMobileNumber } from "@/utils/validation/validate-mobile-number";

const normalizeRequestNationalId = (
	value: string | null | undefined,
	country: NationalIdCountry
): string | null => {
	if (value == null || value.trim() === "") return null;

	return normalizeNationalId(country, value);
};

/** `defaultCountry` applies when the form has no dial country. */
export const toE164MobileNumber = (
	value: string | null | undefined,
	mobileCountry: string | null | undefined,
	defaultCountry: NationalIdCountry
): string | null => {
	if (value == null || value.trim() === "") return null;

	const trimmed = value.trim();
	const country = (mobileCountry || defaultCountry) as CountryCode;
	const result = validateMobileNumber(country, trimmed);
	return result.normalized ?? trimmed;
};

/** `defaultCountry` applies when the number has no known dial prefix. */
export const splitMobileForForm = (
	value: string | null | undefined,
	defaultCountry: NationalIdCountry
): Pick<Applicant, "mobileNumber" | "mobileCountry"> => {
	if (value == null || value.trim() === "") {
		return { mobileCountry: defaultCountry, mobileNumber: "" };
	}

	const trimmed = value.trim().replace(/[\s-]/g, "");

	if (trimmed.startsWith("+46")) {
		return {
			mobileCountry: "SE",
			mobileNumber: `0${trimmed.slice(3)}`,
		};
	}
	if (trimmed.startsWith("0046")) {
		return {
			mobileCountry: "SE",
			mobileNumber: `0${trimmed.slice(4)}`,
		};
	}
	if (trimmed.startsWith("+358")) {
		return {
			mobileCountry: "FI",
			mobileNumber: `0${trimmed.slice(4)}`,
		};
	}
	if (trimmed.startsWith("00358")) {
		return {
			mobileCountry: "FI",
			mobileNumber: `0${trimmed.slice(5)}`,
		};
	}

	return {
		mobileCountry: defaultCountry,
		mobileNumber: value.trim(),
	};
};

const prepareApplicant = (
	person: Partial<Applicant> | undefined,
	country: NationalIdCountry
): Partial<Applicant> | undefined => {
	if (!person) return person;

	const { mobileCountry, mobileNumber, nationalId, ...rest } = person;
	const e164 = toE164MobileNumber(mobileNumber, mobileCountry, country);
	const normalizedNationalId = normalizeRequestNationalId(
		nationalId,
		country
	);

	return {
		...rest,
		...(normalizedNationalId != null || nationalId != null
			? { nationalId: normalizedNationalId ?? nationalId }
			: {}),
		...(e164 != null || mobileNumber != null
			? { mobileNumber: e164 ?? mobileNumber }
			: {}),
	};
};

/** `country` is the validation country of the market (`validationCountry`). */
export const prepareCacheFormsForApi = (
	forms: CacheForms,
	country: NationalIdCountry
): CacheForms => {
	const personalApplicant = prepareApplicant(
		forms.personalInformationForm?.applicant,
		country
	);
	const coApplicantPerson = prepareApplicant(
		forms.economyForm?.coApplicant?.applicant,
		country
	);

	return {
		...forms,
		personalInformationForm: forms.personalInformationForm
			? {
					...forms.personalInformationForm,
					applicant: (personalApplicant ??
						forms.personalInformationForm.applicant) as Applicant,
				}
			: forms.personalInformationForm,
		economyForm: forms.economyForm
			? {
					...forms.economyForm,
					coApplicant: forms.economyForm.coApplicant
						? {
								...forms.economyForm.coApplicant,
								applicant: (coApplicantPerson ??
									forms.economyForm.coApplicant
										.applicant) as Applicant,
							}
						: forms.economyForm.coApplicant,
				}
			: forms.economyForm,
	};
};
